'use client';

import './styles.css';

import { usePathname } from 'next/navigation';
import { useRouter } from 'nextjs-toploader/app';

import NumberFlow from '@number-flow/react';
import { useBoolean, useError } from 'codekit';
import { motion } from 'framer-motion';
import Stripe from 'stripe';
import { Errors } from 'next-bricks';

import Icon from 'components/Icon';
import { routes } from 'config/routes';
import { STAGGER_DELAY, TRANSITION_DURATION } from 'constants/motion';
import { TRIAL_PERIOD_IN_DAYS } from 'constants/pricing';
import { useAuth } from 'contexts/AuthContext';
import Button from 'layouts/institucional/Button';
import { Plan as PlanType, PlanPeriod } from 'models/Plan';
import { api } from 'services/api';
import { LuminousAnimationCard } from '../LuminousAnimation';

import styles from './styles.module.css';

interface PlanProps {
	plan: PlanType;
	period: PlanPeriod;
	highligh?: boolean;
	delayIndex: number;
}

function Plan({ plan, period, highligh, delayIndex }: PlanProps) {
	// Hooks
	const router = useRouter();
	const auth = useAuth();
	const error = useError();
	const pathname = usePathname();

	// Boolean hooks
	const isLoading = useBoolean();

	// Functions
	async function handleSelectPlan() {
		error.clear();
		isLoading.setTrue();

		const login = routes.get('auth.login');

		if (plan.price[period] === 0) {
			return router.push(login);
		}

		if (!auth.isAuthenticated) {
			return router.push(login + '?origin=' + pathname);
		}

		await handleCreateSession()
			.catch(error.catcher)
			.finally(isLoading.setFalse);
	}

	async function handleCreateSession() {
		const payload = {
			planId: plan.id,
			priceId: plan.priceId[period],
		};

		const session = await api
			.post<Stripe.Checkout.Session>('/stripe/checkout', payload)
			.then((res) => res.data);
		if (!session.url) throw new Error('URL de sessão não encontrada');

		const link = document.createElement('a');
		link.href = session.url ?? '';
		link.click();
	}

	return (
		<motion.div
			initial={{
				opacity: 0,
				y: 20,
				filter: 'blur(10px)',
			}}
			animate={{
				opacity: 1,
				y: 0,
				filter: 'blur(0px)',
			}}
			transition={{
				delay: delayIndex * STAGGER_DELAY,
				duration: TRANSITION_DURATION,
			}}
			style={{
				height: '100%',
			}}
		>
			<div>
				<LuminousAnimationCard
					className={styles.container}
					data-highlight={!!highligh}
				>
					<div className={styles.heading}>
						{/* {period === 'yearly' && (
        					<Tooltip
        						label={`Apenas ${(plan.price.yearly / 12).toCurrency()} por mês`}
        						position="left"
        					>
        						<Institutional.Tag>
        							-{discountPercentage}% OFF
        						</Institutional.Tag>
        					</Tooltip>
        				)} */}
						<span>{plan.name}</span>
						<div className={styles.value}>
							<NumberFlow
								value={
									period === 'monthly'
										? plan.price[period]
										: plan.price[period] / 12
								}
								format={{
									currency: 'BRL',
									style: 'currency',
								}}
								suffix=" / mês"
							/>
						</div>
						<p>{plan.description}</p>
					</div>

					<ul className={styles.features}>
						{Object.values(plan.features ?? {})
							.order('listIndex', 'asc')
							.map((feature) => (
								<li key={feature.id}>
									<Icon name="Check" />
									<span>{feature.label}</span>
								</li>
							))}
					</ul>

					<div className={styles.footering}>
						{!error.exists && TRIAL_PERIOD_IN_DAYS > 0 && (
							<span>
								Teste {TRIAL_PERIOD_IN_DAYS} dias grátis
							</span>
						)}

						<Errors errors={error.errors} />

						<Button
							onClick={handleSelectPlan}
							loading={isLoading.value}
							variant={highligh ? 'default' : 'outlined'}
						>
							Selecionar plano
						</Button>
					</div>
				</LuminousAnimationCard>
			</div>
		</motion.div>
	);
}

export default Plan;
